import React, { useState, useRef, useCallback, useEffect } from 'react'
import parse from 'html-react-parser'
import SignatureCanvas from 'react-signature-canvas'
import { Checkbox, InputField } from '@enamelai/design-system'
import { sanitizeHtmlContent } from '@utils/sanitizeHtml.util'
import type { GroupField, FormField, FormRowField } from '../../types'
import { choiceOptionsForField } from '../../createFormField'
import './SchemaFormRenderer.css'
import '../../FormContentArea/FormPreview.css'

const FieldGroupContext = React.createContext<string | undefined>(undefined)

/** Wraps a labelled control and shares its id with the label and control. */
const FieldGroup: React.FC<{
  controlId?: string
  className?: string
  children?: React.ReactNode
}> = ({ controlId, className, children }) => (
  <FieldGroupContext.Provider value={controlId}>
    <div className={className}>{children}</div>
  </FieldGroupContext.Provider>
)

const FieldLabel: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const controlId = React.useContext(FieldGroupContext)
  return (
    <label className="form-label" htmlFor={controlId}>
      {children}
    </label>
  )
}

const FieldTextarea: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
> = ({ className, ...rest }) => {
  const controlId = React.useContext(FieldGroupContext)
  return (
    <textarea
      id={controlId}
      className={['form-control', className].filter(Boolean).join(' ')}
      {...rest}
    />
  )
}

const FieldSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({
  className,
  ...rest
}) => {
  const controlId = React.useContext(FieldGroupContext)
  return (
    <select
      id={controlId}
      className={['form-select', className].filter(Boolean).join(' ')}
      {...rest}
    />
  )
}

const FieldRadio: React.FC<
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
    label: React.ReactNode
  }
> = ({ className, id, label, ...rest }) => (
  <div className={['form-check', className].filter(Boolean).join(' ')}>
    <input className="form-check-input" type="radio" id={id} {...rest} />
    <label className="form-check-label" htmlFor={id}>
      {label}
    </label>
  </div>
)
const looksLikeHtml = (s: string): boolean => /<[a-z][\s\S]*>/i.test(s.trim())

/** String, checkbox boolean, or selected labels for a checkbox group. */
export type SchemaFormFieldValue = string | boolean | string[]
/** Field id -> value. Used for values and binding. */
export type SchemaFormFieldValues = Record<string, SchemaFormFieldValue>
export type SchemaFormReadOnlyDisplayMode = 'default' | 'print'

const isDataUrl = (value: string | unknown): boolean =>
  Boolean(value && typeof value === 'string' && value.startsWith('data:'))

/** Drop a stored hint so a blank fill-in field prints as an empty line. */
function printableAnswer(value: string, hints: readonly string[]): string {
  const trimmed = value.trim()
  if (trimmed.length === 0) return ''
  const isHint = hints.some((hint) => {
    const normalized = hint.trim()
    return normalized.length > 0 && normalized === trimmed
  })
  return isHint ? '' : value
}

const toDesignSystemDate = (value: string): string => {
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  return isoMatch ? `${isoMatch[2]} / ${isoMatch[3]} / ${isoMatch[1]}` : value
}

const fromDesignSystemDate = (value: string): string => {
  const displayMatch = /^(\d{2})\s*\/\s*(\d{2})\s*\/\s*(\d{4})$/.exec(
    value.trim(),
  )
  return displayMatch
    ? `${displayMatch[3]}-${displayMatch[1]}-${displayMatch[2]}`
    : value
}

interface InlineSignaturePadProps {
  fieldId: string
  value: string
  signerName: string
  invalid: boolean
  onChange: (fieldId: string, value: SchemaFormFieldValue) => void
}

/** Signing surface rendered in the form. Strokes are saved when the pen lifts. */
const InlineSignaturePad: React.FC<InlineSignaturePadProps> = ({
  fieldId,
  value,
  signerName,
  invalid,
  onChange,
}) => {
  const canvasRef = useRef<SignatureCanvas>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const savedValueRef = useRef<string>(isDataUrl(value) ? value : '')
  const [isEmpty, setIsEmpty] = useState(!isDataUrl(value))

  const commitStroke = (): void => {
    const pad = canvasRef.current
    if (!pad) return
    const hasStrokes = pad.toData().some((group) => group.length > 0)
    if (!hasStrokes) return
    const dataUrl = pad.toDataURL('image/png')
    if (!isDataUrl(dataUrl) || dataUrl === savedValueRef.current) return
    savedValueRef.current = dataUrl
    setIsEmpty(false)
    onChange(fieldId, dataUrl)
  }

  useEffect(() => {
    const pad = canvasRef.current
    if (!pad || !isDataUrl(value)) return
    if (value === savedValueRef.current && !pad.isEmpty()) return
    const canvas = pad.getCanvas()
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    pad.fromDataURL(value, {
      width: canvas.offsetWidth || canvas.width / ratio,
      height: canvas.offsetHeight || canvas.height / ratio,
      ratio,
    })
    savedValueRef.current = value
    setIsEmpty(false)
  }, [value])

  const handleBegin = (): void => {
    wrapRef.current?.setAttribute('data-signing', 'true')
  }

  const handleClear = (): void => {
    canvasRef.current?.clear()
    wrapRef.current?.removeAttribute('data-signing')
    savedValueRef.current = ''
    setIsEmpty(true)
    onChange(fieldId, '')
  }

  const canvasClassName = 'schema-form-renderer-inline-signature-canvas'
  const wrapClassName = [
    'schema-form-renderer-inline-signature-canvas-wrap',
    invalid
      ? 'schema-form-renderer-inline-signature-canvas-wrap-invalid'
      : null,
  ]
    .filter((className): className is string => className != null)
    .join(' ')

  return (
    <div className="schema-form-renderer-inline-signature">
      <div ref={wrapRef} className={wrapClassName}>
        {isEmpty ? (
          <span className="schema-form-renderer-inline-signature-hint">
            Sign here
          </span>
        ) : null}
        <SignatureCanvas
          ref={canvasRef}
          canvasProps={{
            className: canvasClassName,
            'aria-label': 'Draw your signature',
            tabIndex: 0,
          }}
          penColor="#000000"
          minWidth={1}
          maxWidth={2.5}
          clearOnResize={false}
          onBegin={handleBegin}
          onEnd={commitStroke}
        />
        {isEmpty ? null : (
          <button
            type="button"
            className="schema-form-renderer-inline-signature-clear"
            onClick={handleClear}
            title="Clear signature"
            aria-label="Clear signature"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        <span
          className={
            signerName
              ? 'schema-form-renderer-inline-signature-name'
              : 'schema-form-renderer-inline-signature-name schema-form-renderer-inline-signature-name-placeholder'
          }
        >
          {signerName || 'Patient name'}
        </span>
      </div>
    </div>
  )
}

const isGroupField = (
  item: FormField | GroupField | FormRowField,
): item is GroupField => 'fields' in item && 'name' in item

const isFormRowField = (
  item: FormField | GroupField | FormRowField,
): item is FormRowField =>
  'type' in item && item.type === 'Form Row' && 'fields' in item

const isRichTextOnlyGroup = (group: GroupField): boolean => {
  if (group.fields.length !== 1) return false
  const item = group.fields[0]
  return (
    item != null &&
    !isGroupField(item) &&
    !isFormRowField(item) &&
    (item as FormField).type === 'Rich Text'
  )
}

function formRowCellStyle(colSize: number): React.CSSProperties {
  const span = Math.min(12, Math.max(1, colSize))
  return {
    gridColumn: `span ${span}`,
    minWidth: 0,
  }
}

export interface SchemaFormRendererProps {
  groups: GroupField[]
  /** Form title shown above groups. Omit or empty to hide title block. */
  formTitle?: string
  /** Current values by field id. Used for controlled inputs and display. */
  fieldValues?: SchemaFormFieldValues
  /** When true, all inputs are disabled and no onChange is fired. */
  readOnly?: boolean
  /**
   * When true, Rich Text fields behave as read-only, even if the rest of the
   * form is editable. Intended for patient-facing flows where Rich Text is
   * static paragraph content authored in the builder.
   */
  richTextReadOnly?: boolean
  /** When not readOnly, called when a field value changes (e.g. kiosk editing). */
  onFieldChange?: (fieldId: string, value: SchemaFormFieldValue) => void
  /** Placeholder for empty Rich Text in readOnly mode (e.g. builder preview lorem). */
  richTextEmptyPlaceholder?: string
  /** Field id -> validation error message. */
  validationErrors?: Record<string, string>
  /** When true, show validation error UI. */
  showValidation?: boolean
  /** When provided, shown below the signature line for signature fields (e.g. current patient name). */
  patientName?: string
  /** Visual mode for read-only rendering (default box UI vs print-like line UI). */
  readOnlyDisplayMode?: SchemaFormReadOnlyDisplayMode
  /** Render editable Text Area fields with the design-system message input. */
  useDesignSystemTextArea?: boolean
  /** Patient-facing branded header (logo tile, title and clinic name). */
  brandHeader?: {
    logoSrc: string
    clinicName?: string
  }
}

const SchemaFormRenderer: React.FC<SchemaFormRendererProps> = ({
  groups,
  formTitle = '',
  fieldValues = {},
  readOnly = true,
  richTextReadOnly = false,
  onFieldChange,
  richTextEmptyPlaceholder,
  validationErrors = {},
  showValidation = false,
  patientName: patientNameProp,
  readOnlyDisplayMode = 'default',
  useDesignSystemTextArea = false,
  brandHeader,
}) => {
  const getValue = useCallback(
    (fieldId: string): string =>
      (fieldValues[fieldId] != null ? String(fieldValues[fieldId]) : '') || '',
    [fieldValues],
  )

  const getCheckboxChecked = useCallback(
    (fieldId: string): boolean => fieldValues[fieldId] === true,
    [fieldValues],
  )

  const getSelectedOptions = useCallback(
    (fieldId: string): string[] => {
      const raw = fieldValues[fieldId]
      if (!Array.isArray(raw)) return []
      return raw.filter((item): item is string => typeof item === 'string')
    },
    [fieldValues],
  )

  const handleFieldChange = useCallback(
    (fieldId: string, value: SchemaFormFieldValue): void => {
      if (readOnly || !onFieldChange) return
      onFieldChange(fieldId, value)
    },
    [readOnly, onFieldChange],
  )

  const isPrintReadOnlyMode = readOnly && readOnlyDisplayMode === 'print'

  const renderPrintLine = (
    controlId: string,
    label: string,
    display: string,
  ): React.ReactElement => (
    <FieldGroup key={controlId} controlId={controlId}>
      <FieldLabel>{label}</FieldLabel>
      <div className="schema-form-renderer-print-line-field">
        <span
          className={
            display
              ? 'schema-form-renderer-print-line-value'
              : 'schema-form-renderer-print-line-value schema-form-renderer-print-line-value-empty'
          }
        >
          {display || '\u00A0'}
        </span>
      </div>
    </FieldGroup>
  )

  const renderFieldError = (message: string | undefined): React.ReactNode => {
    if (message == null || message.length === 0) return null
    return (
      <div className="schema-form-renderer-field-error" role="alert">
        {message}
      </div>
    )
  }

  const renderField = (
    field: FormField,
    options?: { hideLabel?: boolean },
  ): React.ReactNode => {
    const fieldLabel = field.label || field.type
    const value = getValue(field.id)
    const fillInHints = [
      field.placeholder ?? '',
      `Enter ${fieldLabel.toLowerCase()}`,
    ]
    const errorMessage = showValidation ? validationErrors[field.id] : undefined
    const showError = errorMessage != null && errorMessage.length > 0

    switch (field.type) {
      case 'Input Field':
        if (isPrintReadOnlyMode) {
          return (
            <FieldGroup key={field.id} controlId={`readonly-input-${field.id}`}>
              <FieldLabel>{fieldLabel}</FieldLabel>
              <div className="schema-form-renderer-print-line-field">
                <span
                  className={
                    value
                      ? 'schema-form-renderer-print-line-value'
                      : 'schema-form-renderer-print-line-value schema-form-renderer-print-line-value-empty'
                  }
                >
                  {printableAnswer(value, [...fillInHints, 'Enter text']) ||
                    '\u00A0'}
                </span>
              </div>
            </FieldGroup>
          )
        }
        return (
          <InputField
            key={field.id}
            variant="text"
            fullWidth
            label={fieldLabel}
            optional={field.required !== true}
            placeholder={
              readOnly
                ? ''
                : field.placeholder || `Enter ${fieldLabel.toLowerCase()}`
            }
            value={value}
            onChange={
              readOnly
                ? undefined
                : (e) => handleFieldChange(field.id, e.target.value)
            }
            disabled={readOnly}
            required={field.required ?? false}
            error={showError}
            helperText={errorMessage}
          />
        )

      case 'Date Field':
        if (isPrintReadOnlyMode) {
          return (
            <FieldGroup key={field.id} controlId={`readonly-date-${field.id}`}>
              <FieldLabel>{fieldLabel}</FieldLabel>
              <div className="schema-form-renderer-print-line-field">
                <span
                  className={
                    value
                      ? 'schema-form-renderer-print-line-value'
                      : 'schema-form-renderer-print-line-value schema-form-renderer-print-line-value-empty'
                  }
                >
                  {printableAnswer(value, [...fillInHints, 'Select date']) ||
                    '\u00A0'}
                </span>
              </div>
            </FieldGroup>
          )
        }
        return (
          <InputField
            key={field.id}
            variant="date"
            fullWidth
            label={fieldLabel}
            optional={field.required !== true}
            value={toDesignSystemDate(value)}
            onChange={
              readOnly
                ? undefined
                : (e) =>
                    handleFieldChange(
                      field.id,
                      fromDesignSystemDate(e.target.value),
                    )
            }
            disabled={readOnly}
            required={field.required ?? false}
            error={showError}
            helperText={errorMessage}
          />
        )

      case 'Checkbox':
        return (
          <FieldGroup
            key={field.id}
            controlId={`checkbox-${field.id}`}
            className="schema-form-renderer-checkbox-field"
          >
            <Checkbox
              id={`checkbox-${field.id}`}
              label={fieldLabel}
              disabled={readOnly}
              checked={getCheckboxChecked(field.id)}
              className={showError ? 'schema-form-renderer-field-invalid' : ''}
              required={field.required ?? false}
              aria-invalid={showError || undefined}
              onChange={
                readOnly || !onFieldChange
                  ? undefined
                  : (e) => handleFieldChange(field.id, e.target.checked)
              }
            />
            {showValidation && field.required === true && (
              <div
                className={`schema-form-renderer-field-error ${
                  showError ? '' : 'schema-form-renderer-field-error--reserved'
                }`}
                role={showError ? 'alert' : undefined}
                aria-hidden={!showError}
              >
                {showError ? errorMessage : '\u00A0'}
              </div>
            )}
          </FieldGroup>
        )

      case 'Rich Text': {
        const displayValue =
          value && value.length > 0
            ? value
            : field.placeholder && field.placeholder.length > 0
              ? field.placeholder
              : readOnly && richTextEmptyPlaceholder != null
                ? richTextEmptyPlaceholder
                : ''
        const richTextDisabled = readOnly || richTextReadOnly
        const showAsHtml = richTextDisabled && looksLikeHtml(displayValue)
        const showAsDataUrlImage =
          richTextDisabled && !showAsHtml && isDataUrl(displayValue)
        const showAsPlainText =
          richTextDisabled && !showAsHtml && !showAsDataUrlImage
        return (
          <FieldGroup key={field.id} controlId={`richtext-${field.id}`}>
            {!options?.hideLabel && <FieldLabel>{fieldLabel}</FieldLabel>}
            {showAsHtml ? (
              <div
                className="form-preview-richtext-content"
                data-testid={`richtext-content-${field.id}`}
              >
                {parse(sanitizeHtmlContent(displayValue))}
              </div>
            ) : showAsDataUrlImage ? (
              <div
                className="form-preview-richtext-content"
                data-testid={`richtext-content-${field.id}`}
              >
                <img src={displayValue} alt="" />
              </div>
            ) : showAsPlainText ? (
              <div
                className="form-preview-richtext-content"
                data-testid={`richtext-content-${field.id}`}
              >
                {displayValue}
              </div>
            ) : (
              <FieldTextarea
                rows={5}
                placeholder={
                  field.placeholder || `Enter ${fieldLabel.toLowerCase()}`
                }
                disabled={richTextDisabled}
                className={`form-preview-textarea ${
                  showError ? 'schema-form-renderer-field-invalid' : ''
                }`}
                value={displayValue}
                onChange={
                  richTextDisabled || !onFieldChange
                    ? undefined
                    : (e) => handleFieldChange(field.id, e.target.value)
                }
              />
            )}
            {showError && (
              <div className="schema-form-renderer-field-error" role="alert">
                {errorMessage}
              </div>
            )}
          </FieldGroup>
        )
      }

      case 'Text Area':
        if (isPrintReadOnlyMode) {
          return renderPrintLine(
            `readonly-textarea-${field.id}`,
            fieldLabel,
            printableAnswer(value, [...fillInHints, 'Write a message']),
          )
        }
        if (useDesignSystemTextArea) {
          return (
            <div key={field.id} className="schema-form-renderer-ds-textarea">
              <InputField
                id={`textarea-${field.id}`}
                variant="message"
                fullWidth
                label={fieldLabel}
                optional={field.required !== true}
                rows={4}
                maxLength={100}
                showCounter
                placeholder={
                  readOnly
                    ? ''
                    : field.placeholder || `Enter ${fieldLabel.toLowerCase()}`
                }
                disabled={readOnly}
                required={field.required ?? false}
                error={showError}
                value={value}
                onChange={
                  readOnly || !onFieldChange
                    ? undefined
                    : (e) => handleFieldChange(field.id, e.target.value)
                }
              />
              {renderFieldError(errorMessage)}
            </div>
          )
        }
        return (
          <FieldGroup key={field.id} controlId={`textarea-${field.id}`}>
            <FieldLabel>{fieldLabel}</FieldLabel>
            <FieldTextarea
              rows={4}
              placeholder={
                readOnly
                  ? ''
                  : field.placeholder || `Enter ${fieldLabel.toLowerCase()}`
              }
              disabled={readOnly}
              required={field.required ?? false}
              className={`form-preview-textarea ${
                showError ? 'schema-form-renderer-field-invalid' : ''
              }`}
              value={value}
              onChange={
                readOnly || !onFieldChange
                  ? undefined
                  : (e) => handleFieldChange(field.id, e.target.value)
              }
            />
            {renderFieldError(errorMessage)}
          </FieldGroup>
        )

      case 'Dropdown': {
        const dropdownOptions = choiceOptionsForField(field)
        if (isPrintReadOnlyMode) {
          return renderPrintLine(
            `readonly-dropdown-${field.id}`,
            fieldLabel,
            printableAnswer(value, [field.placeholder ?? '', 'Select']),
          )
        }
        return (
          <FieldGroup key={field.id} controlId={`dropdown-${field.id}`}>
            <FieldLabel>{fieldLabel}</FieldLabel>
            <FieldSelect
              value={value}
              disabled={readOnly}
              className={`form-preview-select ${
                showError ? 'schema-form-renderer-field-invalid' : ''
              }`}
              required={field.required ?? false}
              onChange={
                readOnly || !onFieldChange
                  ? undefined
                  : (e) => handleFieldChange(field.id, e.target.value)
              }
            >
              <option value="">{field.placeholder || 'Select'}</option>
              {dropdownOptions.map((option, index) => (
                <option key={`${option}-${index}`} value={option}>
                  {option}
                </option>
              ))}
            </FieldSelect>
            {renderFieldError(errorMessage)}
          </FieldGroup>
        )
      }

      case 'Checkbox Group': {
        const checkboxOptions = choiceOptionsForField(field)
        const selected = getSelectedOptions(field.id)
        if (isPrintReadOnlyMode) {
          return renderPrintLine(
            `readonly-checkbox-group-${field.id}`,
            fieldLabel,
            selected.join(', '),
          )
        }
        return (
          <FieldGroup key={field.id} controlId={`checkbox-group-${field.id}`}>
            <FieldLabel>{fieldLabel}</FieldLabel>
            <div className="form-preview-checkbox-group">
              {checkboxOptions.map((option, index) => (
                <Checkbox
                  key={`${option}-${index}`}
                  id={`checkbox-group-${field.id}-${index}`}
                  label={option}
                  disabled={readOnly}
                  checked={selected.includes(option)}
                  className={
                    showError ? 'schema-form-renderer-field-invalid' : ''
                  }
                  aria-invalid={showError || undefined}
                  onChange={
                    readOnly || !onFieldChange
                      ? undefined
                      : () => {
                          const next = selected.includes(option)
                            ? selected.filter((item) => item !== option)
                            : [...selected, option]
                          handleFieldChange(field.id, next)
                        }
                  }
                />
              ))}
            </div>
            {showValidation && field.required === true && (
              <div
                className={`schema-form-renderer-field-error ${
                  showError ? '' : 'schema-form-renderer-field-error--reserved'
                }`}
                role={showError ? 'alert' : undefined}
                aria-hidden={!showError}
              >
                {showError ? errorMessage : '\u00A0'}
              </div>
            )}
          </FieldGroup>
        )
      }

      case 'Radio Group':
      case 'Gender Selection': {
        const radioOptions = choiceOptionsForField(field)
        if (isPrintReadOnlyMode) {
          return renderPrintLine(
            `readonly-radio-${field.id}`,
            fieldLabel,
            value,
          )
        }
        return (
          <FieldGroup key={field.id} controlId={`radio-${field.id}`}>
            <FieldLabel>{fieldLabel}</FieldLabel>
            <div className="form-preview-radio-group">
              {radioOptions.map((option, index) => (
                <FieldRadio
                  key={`${option}-${index}`}
                  name={`radio-${field.id}`}
                  id={`radio-${field.id}-${index}`}
                  label={option}
                  disabled={readOnly}
                  checked={value === option}
                  className={`form-preview-radio ${
                    showError ? 'schema-form-renderer-field-invalid' : ''
                  }`}
                  required={field.required ?? false}
                  onChange={
                    readOnly || !onFieldChange
                      ? undefined
                      : () => handleFieldChange(field.id, option)
                  }
                />
              ))}
            </div>
            {renderFieldError(errorMessage)}
          </FieldGroup>
        )
      }

      case 'Attachments':
        return (
          <FieldGroup key={field.id} controlId={`attachment-${field.id}`}>
            <FieldLabel>{fieldLabel}</FieldLabel>
            <div className="form-preview-attachment">
              <button
                type="button"
                className="form-preview-attachment-button"
                disabled
              >
                <i className="mgc mgc_attachment_2_line"></i>
                <span>Select File</span>
              </button>
            </div>
          </FieldGroup>
        )

      case 'Signature Field':
      case 'Dentist/Admin Signature': {
        const hasDrawnSignature = isDataUrl(value)
        const signerNameId = `${field.id}-name`
        const signerNameFromValues =
          (fieldValues[signerNameId] != null
            ? String(fieldValues[signerNameId])
            : '') || ''
        const signerName = signerNameFromValues || patientNameProp || ''

        if (isPrintReadOnlyMode) {
          return (
            <FieldGroup
              key={field.id}
              controlId={`signature-print-${field.id}`}
              className="schema-form-renderer-print-signature-group"
            >
              <FieldLabel>{fieldLabel}</FieldLabel>
              <div className="schema-form-renderer-signature-line-wrap">
                <div className="schema-form-renderer-signature-line">
                  {hasDrawnSignature ? (
                    <img
                      src={value}
                      alt="Signature"
                      className="schema-form-renderer-signature-img schema-form-renderer-print-signature-img"
                    />
                  ) : null}
                </div>
                <div
                  className={
                    signerName
                      ? 'schema-form-renderer-signer-name'
                      : 'schema-form-renderer-signer-name schema-form-renderer-signer-name-placeholder'
                  }
                >
                  {signerName || 'Patient name'}
                </div>
              </div>
              {showError && (
                <div className="schema-form-renderer-field-error" role="alert">
                  {errorMessage}
                </div>
              )}
            </FieldGroup>
          )
        }

        const signerNameNode = (
          <div
            className={
              signerName
                ? 'schema-form-renderer-signer-name'
                : 'schema-form-renderer-signer-name schema-form-renderer-signer-name-placeholder'
            }
          >
            {signerName || 'Patient name'}
          </div>
        )

        if (readOnly) {
          return (
            <FieldGroup
              key={field.id}
              controlId={`signature-${field.id}`}
              className="schema-form-renderer-signature-field"
            >
              <FieldLabel>{fieldLabel}</FieldLabel>
              <div
                className={`form-preview-signature ${
                  showError ? 'schema-form-renderer-field-invalid' : ''
                }`}
              >
                <div className="schema-form-renderer-signature-wrap">
                  <div className="form-preview-signature-box">
                    {hasDrawnSignature ? (
                      <img
                        src={value}
                        alt="Signature"
                        className="schema-form-renderer-signature-img"
                      />
                    ) : (
                      <span className="form-preview-signature-placeholder">
                        Signature
                      </span>
                    )}
                  </div>
                </div>
                <div className="schema-form-renderer-signature-line-wrap">
                  <div className="schema-form-renderer-signature-line" />
                  {signerNameNode}
                </div>
                {showError && (
                  <div
                    className="schema-form-renderer-field-error"
                    role="alert"
                  >
                    {errorMessage}
                  </div>
                )}
              </div>
            </FieldGroup>
          )
        }

        return (
          <FieldGroup
            key={field.id}
            controlId={`signature-${field.id}`}
            className="schema-form-renderer-signature-field"
          >
            <FieldLabel>{fieldLabel}</FieldLabel>
            <div className="form-preview-signature">
              <InlineSignaturePad
                fieldId={field.id}
                value={value}
                signerName={signerName}
                invalid={showError}
                onChange={handleFieldChange}
              />
              {showError && (
                <div className="schema-form-renderer-field-error" role="alert">
                  {errorMessage}
                </div>
              )}
            </div>
          </FieldGroup>
        )
      }

      default:
        return null
    }
  }

  const renderFieldsInRows = (
    fields: (FormField | GroupField | FormRowField)[],
  ): React.ReactNode => {
    return (
      <>
        {fields.map((item) => {
          if (isFormRowField(item)) {
            const formRow = item
            return (
              <div
                key={formRow.id}
                className="schema-form-form-row form-preview-form-row"
              >
                {formRow.fields.map((field) => {
                  const columnCount = formRow.columnCount === 1 ? 1 : 2
                  const colSize = columnCount === 1 ? 12 : 6
                  const cellClassName = [
                    'schema-form-form-row-cell',
                    field.type === 'Checkbox'
                      ? 'form-preview-form-row-checkbox-col'
                      : null,
                  ]
                    .filter((v): v is string => v != null)
                    .join(' ')
                  return (
                    <div
                      key={field.id}
                      className={cellClassName}
                      style={formRowCellStyle(colSize)}
                    >
                      {renderField(field)}
                    </div>
                  )
                })}
              </div>
            )
          }

          if (isGroupField(item)) {
            const nestedGroup = item
            return (
              <div key={nestedGroup.id} className="form-preview-nested-group">
                <h4 className="form-preview-nested-group-title">
                  {nestedGroup.name}
                </h4>
                {renderFieldsInRows(nestedGroup.fields)}
              </div>
            )
          }

          const rowClassName = [
            'schema-form-form-row-full',
            item.type === 'Rich Text'
              ? 'schema-form-form-row-full--rich-text'
              : null,
          ]
            .filter((value): value is string => value != null)
            .join(' ')

          return (
            <div key={item.id} className={rowClassName}>
              {renderField(item)}
            </div>
          )
        })}
      </>
    )
  }

  const renderGroup = (group: GroupField): React.ReactNode => {
    if (isRichTextOnlyGroup(group)) {
      const field = group.fields[0] as FormField
      return (
        <div key={group.id} className="form-preview-group">
          <form>{renderField(field, { hideLabel: true })}</form>
        </div>
      )
    }
    if (group.standalone === true) {
      return (
        <div
          key={group.id}
          className="form-preview-group form-preview-group-standalone"
        >
          <form>{renderFieldsInRows(group.fields)}</form>
        </div>
      )
    }
    return (
      <div key={group.id} className="form-preview-group">
        <h3 className="form-preview-group-title">{group.name}</h3>
        <form>{renderFieldsInRows(group.fields)}</form>
      </div>
    )
  }

  return (
    <div className="schema-form-renderer form-preview">
      {formTitle ? (
        <div
          className={`form-content-header${brandHeader ? ' form-content-header--brand' : ''}`}
        >
          <div className="form-content-header-left">
            {brandHeader && (
              <img
                className="form-content-header__logo"
                src={brandHeader.logoSrc}
                alt=""
              />
            )}
            <div className="form-content-header__text">
              <h2 className="form-preview-title">{formTitle}</h2>
              {brandHeader?.clinicName && (
                <p className="form-content-header__clinic">
                  {brandHeader.clinicName}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : null}
      <div className="form-content-body">
        {groups.length === 0 ? (
          <div className="form-preview-empty">
            <p>No fields added to this form yet.</p>
          </div>
        ) : (
          <div className="form-preview-groups">
            {groups.map((group) => renderGroup(group))}
          </div>
        )}
      </div>
    </div>
  )
}

export default SchemaFormRenderer
